import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/Log';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";
import {LogGroupQueryRequest, LogGroupQueryResult, TimelineReportRequest, TimelineReportResult} from "../types/models/LogGroups";

export const LOG_API_REDUCER_KEY = 'logs';
export const LOG_PATH = 'logs';

export const logApi = createApi({
    reducerPath: LOG_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['Log', 'Logs'],
    endpoints: (builder) => ({
        getLogList: builder.query<PaginationResult<Entity.Log>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ LOG_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['Logs'],
        }),
        getLog: builder.query<Entity.Log, Entity.PrimaryLog>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = logNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ LOG_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['Log'],
        }),
        getLogMultiple: builder.query<Entity.Log, Entity.PrimaryLog[]>({
            query: (ids) => {
                return ({
                    url: `/${ LOG_PATH }/ids${Entity.PrimaryLog.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['Logs'],
        }),
        getLogPresentation: builder.query<PaginationResult<Entity.Log>, Entity.PresentationLogRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ LOG_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['Logs'],
        }),
        getLogAdvancedPresentation: builder.query<PaginationResult<Entity.Log>, Entity.FilterAdvancedPresentationRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
                return ({
                    url: `/${ LOG_PATH }/find/advancedPresentation?${new URLSearchParams(queryParams).toString()}`,
                    method: 'POST',
                    data: request.filter,
                })
            },
            providesTags: ['Logs'],
        }),
        
        insertLog: builder.mutation<Entity.Log, Entity.CreateLog>({
            query: (toInsert) => {
                return ({
                    url: `/${ LOG_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    logApi.util.upsertQueryData('getLog', Entity.PrimaryLog.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['Logs'],
        }),
        insertMultipleLog: builder.mutation<Entity.Log[], Entity.CreateLog[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ LOG_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            logApi.util.upsertQueryData('getLog', Entity.PrimaryLog.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['Logs'],
        }),
        updateLog: builder.mutation<Entity.Log, Entity.UpdateEntityLog>({
            query: (toUpdate) => {
                return ({
                    url: `/${ LOG_PATH }/${toUpdate.id.toPath()}`,
                    method: 'PUT',
                    data: toUpdate.data,
                })
            },
            invalidatesTags: ['Logs'],
        }),
        updateMultipleLog: builder.mutation<Entity.Log[], Entity.UpdateEntityLog[]>({
            query: (toUpdate) => {
                return ({
                    url: `/${ LOG_PATH }/batch`,
                    method: 'PUT',
                    data: toUpdate,
                })
            },
            invalidatesTags: ['Logs'],
        }),
        deleteLog: builder.mutation<Entity.Log, Entity.Log>({
            query: (toDelete) => {
                return ({
                    url: `/${ LOG_PATH }/${Entity.PrimaryLog.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['Logs'],
        }),
        deleteMultipleLog: builder.mutation<Entity.Log[], Entity.PrimaryLog[]>({
            query: (ids) => {
                return ({
                    url: `/${ LOG_PATH }/batch${Entity.PrimaryLog.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['Logs'],
        }),
        getGroupWithShipments: builder.query<PaginationResult<LogGroupQueryResult>, LogGroupQueryRequest>({
            query: (request) => {

                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
                return ({
                    url: `/${ LOG_PATH }/groupBy/typesWithShipments?${new URLSearchParams(queryParams).toString()}`,
                    method: 'POST',
                    data: request.filter,
                })
            },
            providesTags: (_result, _error, request) => [{ type: 'Log', id: 'Group' }]
        }),
        getGroup: builder.query<Array<LogGroupQueryResult>, LogGroupQueryRequest>({
            query: (request) => {

                function removeNullProperties(obj: Record<string, any>): Record<string, any> {
                    return Object.fromEntries(
                        Object.entries(obj)
                            .filter(([_, value]) => value !== null && value !== undefined && value !== '')
                    );
                }

                const queryParams: Record<string, any> = removeNullProperties(request);
                return ({
                    url: `/${ LOG_PATH }/groupBy/types`,
                    method: 'POST',
                    data: queryParams,
                })
            },
            providesTags: (_result, _error, request) => [{ type: 'Log', id: 'Group' }]
        }),
        uploadShipmentCsv: builder.mutation<any, any>({
            query: (csvFile) => {
                var bodyFormData = new FormData();
                bodyFormData.append('file', csvFile.file);
                return {
                    url: `/${ LOG_PATH }/imports/shipment?cutting_id=${csvFile.cuttingId}&shipment_id=${csvFile.shipmentId}&location_id=${csvFile.locationId}`,
                    method: 'POST',
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    data: bodyFormData,
                    formData: true
                };
            },
            invalidatesTags: ['Logs'],
        }),
        getLogTimelineEntered: builder.query<TimelineReportResult[], TimelineReportRequest>({
            query: (request) => ({
                url: `/${LOG_PATH}/report/timeline/entered`,
                method: 'POST',
                data: request,
            }),
            providesTags: (_result, _error, request) => [{ type: 'Log', id: 'TimelineEntered' }]
        }),
        getLogTimelineShipped: builder.query<TimelineReportResult[], TimelineReportRequest>({
            query: (request) => ({
                url: `/${LOG_PATH}/report/timeline/shipped`,
                method: 'POST',
                data: request,
            }),
            providesTags: (_result, _error, request) => [{ type: 'Log', id: 'TimelineShipped' }]
        }),
        getLogTotal: builder.query<Entity.LogTotalQueryResult[], Entity.LogTotalQueryRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = request;
                return ({
                    url: `/${LOG_PATH}/groupBy/total?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: (_result, _error, _request) => [{ type: 'Log', id: 'Total' }]
        }),
    }),
})

export const [logNormalizer, logNormalizerMiddleware] = createNormalizationMiddleware(LOG_API_REDUCER_KEY, logApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetLogListQuery,
    useGetLogMultipleQuery,
    useGetLogPresentationQuery,
    useGetLogQuery,
    useInsertLogMutation,
    useInsertMultipleLogMutation,
    useUpdateLogMutation,
    useUpdateMultipleLogMutation,
    useDeleteLogMutation,
    useDeleteMultipleLogMutation,
    useGetLogAdvancedPresentationQuery,
    useGetGroupWithShipmentsQuery,
    useGetGroupQuery,
    useUploadShipmentCsvMutation,
    useGetLogTimelineEnteredQuery,
    useGetLogTimelineShippedQuery,
    useGetLogTotalQuery,

} = logApi
